import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/lib/store/auth'
import { Navigation } from '@/components/layout/Navigation'
import { Footer } from '@/components/layout/Footer'
import { AddressStatusBar } from '@/components/layout/AddressStatusBar'
import { ParticleBackground } from '@/components/effects/ParticleBackground'
import { ExperimentalFeatureModal } from '@/components/modals/ExperimentalFeatureModal'
import ErrorBoundary from '@/components/ErrorBoundary'
import { ScrollToTop } from '@/components/ScrollToTop'
import { PageLoader } from '@/components/PageLoader'

// ADD THESE NEW IMPORTS
import { DomainRouter } from '@/components/DomainRouter'
import { getDomainThemeClass, getCurrentDomain, isPublicQuietMode, getBrandMeta, getDomainConfig } from '@/utils/domainConfig'
import { HoldingPage } from '@/pages/HoldingPage'
import { RetiredDomainPage } from '@/pages/RetiredDomainPage'

// The QKey 2026 marketing shell. THIS FILE IS THE ONE THAT SHIPS — vercel.json
// builds `npm run build`, which is the default vite target (index.html →
// src/main.tsx → this file → dist/). The src/qkey/ tree is a SECOND target
// (BUILD_TARGET=qkey → dist-qkey/) that no deployment consumes, so wiring the
// shell there alone would have shipped nothing: the bundle carried 11 dangling
// var(--c-qk-*) references and no font files. Keep the two in step, or verify
// against dist/ rather than dist-qkey/ before believing a change is live.
//
// Imported eagerly rather than lazily so the chrome never suspends. Its CSS is
// entirely scoped under [data-qk-root], so the other brands this file serves
// carry the stylesheet but never render against it, and the @font-face rules
// only fetch when a qk- face is actually used.
import { QkeySiteShell } from '@/qkey/site'

// Lazy load all pages
const HomePageModern = lazy(() => import('@/pages/HomePageModern').then(m => ({ default: m.HomePageModern })))
// qkey.co's 2026 homepage, built on the ported QSAVE design system. Replaces
// HomePageModern on QKey hosts only; the other brands keep their own homepages.
const QkeyHomePage = lazy(() => import('@/qkey/pages/HomePage').then(m => ({ default: m.HomePage })))
// qkey.co's public front door while the full site is in preparation. Carries
// its own header and footer and does NOT mount inside the marketing shell —
// see the file header for why a coming-soon page must not offer the full nav.
const ComingSoonPage = lazy(() => import('@/qkey/pages/ComingSoonPage').then(m => ({ default: m.ComingSoonPage })))

// ADD THESE NEW DOMAIN-SPECIFIC HOMEPAGE IMPORTS
const QSAVEHomePage = lazy(() => import('@/pages/domains/QSAVEHomePage').then(m => ({ default: m.QSAVEHomePage })))
// Catch-all for unmatched paths (repairs trailing punctuation, else 404s).
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const QDayHomePage = lazy(() => import('@/pages/domains/QDayHomePage').then(m => ({ default: m.QDayHomePage })))
const PQPHomePage = lazy(() => import('@/pages/domains/PQPHomePage').then(m => ({ default: m.PQPHomePage })))

// ... rest of your page imports stay the same ...
const PQPPage = lazy(() => import('@/pages/PQPPage').then(m => ({ default: m.PQPPage })))
const QuantumKeysPage = lazy(() => import('@/pages/QuantumKeysPage').then(m => ({ default: m.QuantumKeysPage })))
const HDWalletPage = lazy(() => import('@/pages/HDWalletPage').then(m => ({ default: m.HDWalletPage })))
const QD3ProtectionPage = lazy(() => import('@/pages/QD3ProtectionPage').then(m => ({ default: m.QD3ProtectionPage })))
const QuantumVaultPage = lazy(() => import('@/pages/QuantumVaultPage').then(m => ({ default: m.QuantumVaultPage })))
const QuantumVaultPQPPage = lazy(() => import('@/pages/QuantumVaultPQPPage').then(m => ({ default: m.QuantumVaultPQPPage })))
const TransactionPage = lazy(() => import('@/pages/TransactionPage').then(m => ({ default: m.TransactionPage })))
const FAQPage = lazy(() => import('@/pages/FAQPage').then(m => ({ default: m.FAQPage })))
const AboutPage = lazy(() => import('@/pages/AboutPage').then(m => ({ default: m.AboutPage })))
const WhitepapersPage = lazy(() => import('@/pages/WhitepapersPage').then(m => ({ default: m.WhitepapersPage })))
const TermsPage = lazy(() => import('@/pages/TermsPage').then(m => ({ default: m.TermsPage })))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const InvoiceGeneratorPage = lazy(() => import('@/pages/InvoiceGeneratorPage').then(m => ({ default: m.InvoiceGeneratorPage })))
const CertificationProgramPage = lazy(() => import('@/pages/CertificationProgramPage').then(m => ({ default: m.CertificationProgramPage })))
const ToolsPage = lazy(() => import('@/pages/ToolsPage').then(m => ({ default: m.ToolsPage })))
const BrandGuidelinesPage = lazy(() => import('@/pages/BrandGuidelinesPage').then(m => ({ default: m.BrandGuidelinesPage })))
const TokenomicsPage = lazy(() => import('@/pages/TokenomicsPage').then(m => ({ default: m.TokenomicsPage })))
const QChainPage = lazy(() => import('@/pages/QChainPage').then(m => ({ default: m.QChainPage })))
const PhilanthropicFoundationPage = lazy(() => import('@/pages/PhilanthropicFoundationPage').then(m => ({ default: m.PhilanthropicFoundationPage })))
const MultiWalletDashboard = lazy(() => import('@/pages/MultiWalletDashboard').then(m => ({ default: m.MultiWalletDashboard })))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const PermissionlessPage = lazy(() => import('@/pages/PermissionlessPage'))
const WaterDropPage = lazy(() => import('@/pages/WaterDropPage'))
const TokenPage = lazy(() => import('@/pages/TokenPage').then(m => ({ default: m.TokenPage })))
const BitcoinToolsPage = lazy(() => import('@/pages/BitcoinToolsPage').then(m => ({ default: m.BitcoinToolsPage })))
const WhitepaperViewerPage = lazy(() => import('@/pages/WhitepaperViewerPage').then(m => ({ default: m.WhitepaperViewerPage })))
const ImageTest = lazy(() => import('@/components/debug/ImageTest').then(m => ({ default: m.ImageTest })))
const ProtectionWorkflowPage = lazy(() => import('@/pages/ProtectionWorkflowPage').then(m => ({ default: m.ProtectionWorkflowPage })))
const RecoveryPage = lazy(() => import('@/pages/RecoveryPage').then(m => ({ default: m.RecoveryPage })))
const AutomaticMigrationPage = lazy(() => import('@/pages/AutomaticMigrationPage').then(m => ({ default: m.AutomaticMigrationPage })))
const RegisterPage = lazy(() => import('@/pages/RegisterPage').then(m => ({ default: m.RegisterPage })))
const ImplementersPage = lazy(() => import('@/pages/ImplementersPage').then(m => ({ default: m.ImplementersPage })))
const WalletHardeningPage = lazy(() => import('@/pages/WalletHardeningPage').then(m => ({ default: m.WalletHardeningPage })))
const QSavePage = lazy(() => import('@/pages/QSavePage').then(m => ({ default: m.QSavePage })))
const TechnicalDocsPage = lazy(() => import('@/pages/TechnicalDocsPage').then(m => ({ default: m.TechnicalDocsPage })))
const OutputAnalyzerTestPage = lazy(() => import('@/pages/OutputAnalyzerTestPage').then(m => ({ default: m.OutputAnalyzerTestPage })))
const KycCompliancePage = lazy(() => import('@/pages/token/KycCompliancePage').then(m => ({ default: m.KycCompliancePage })))
const RestrictedCountriesPage = lazy(() => import('@/pages/token/RestrictedCountriesPage').then(m => ({ default: m.RestrictedCountriesPage })))
const GeoBlockedPage = lazy(() => import('@/pages/GeoBlockedPage').then(m => ({ default: m.GeoBlockedPage })))
const RegulatoryInfoPage = lazy(() => import('@/pages/token/RegulatoryInfoPage').then(m => ({ default: m.RegulatoryInfoPage })))
const AddressTestTablePage = lazy(() => import('@/pages/AddressTestTablePage').then(m => ({ default: m.AddressTestTablePage }))) // Extended test suite
const USPQPage = lazy(() => import('@/pages/USPQPage').then(m => ({ default: m.USPQPage })))
const PartnersPage = lazy(() => import('@/pages/PartnersPage').then(m => ({ default: m.PartnersPage })))
const StandardsMarkPage = lazy(() => import('@/pages/StandardsMarkPage').then(m => ({ default: m.StandardsMarkPage })))
const StandardsMarkContactPage = lazy(() => import('@/pages/StandardsMarkContactPage').then(m => ({ default: m.StandardsMarkContactPage })))
const EnterprisePage = lazy(() => import('@/pages/EnterprisePage').then(m => ({ default: m.EnterprisePage })))
const DocumentViewer = lazy(() => import('@/components/DocumentViewer').then(m => ({ default: m.DocumentViewer })))
const LatticeCryptoVisualization = lazy(() => import('@/pages/LatticeCryptoVisualization'))
const TestReportPage = lazy(() => import('@/pages/TestReportPage').then(m => ({ default: m.TestReportPage })))
// Temporarily disable lazy loading for ContactPage to fix crash
import { ContactPage } from '@/pages/ContactPage'
// const ContactPage = lazy(() => import('@/pages/ContactPage').then(m => ({ default: m.ContactPage })))

/**
 * The chrome switch — which shell wraps the routes.
 *
 * This one file serves every brand by hostname, so the chrome has to branch
 * here rather than per page. Two shells:
 *
 *   QKEY (qkey.co, staging.qkey.co, localhost) — the 2026 marketing shell. It
 *   owns the nav, footer, theme boundary, skip link and ground, so every QKey
 *   route looks like one site. This is the fix for the split it replaces: the
 *   old qkey.co homepage carried its own bespoke <nav> and <footer> inside the
 *   page component while every inner page used the global app chrome, so the
 *   site shipped two unrelated designs depending on where you landed.
 *   ParticleBackground is NOT rendered inside it — the shell has its own
 *   atmosphere layer (grain + hero glow) and running both puts two decorative
 *   canvases behind one page.
 *
 *   EVERY OTHER BRAND (qsave.org, qday.is, pqp.to) — the existing app chrome,
 *   untouched. QSAVE has its own editorial design in this codebase and the
 *   satellites are separate brands; dressing them in QKey's indigo nav would be
 *   a rebrand nobody asked for.
 *
 * AddressStatusBar and ExperimentalFeatureModal are FUNCTIONAL, not chrome, so
 * they render in both. `pb-12` is what keeps content clear of the fixed status
 * bar, so both branches carry it.
 */
function Chrome({
  isQkeyBrand,
  bare,
  themeClass,
  children,
}: {
  isQkeyBrand: boolean
  /**
   * Render the route with NO shared chrome at all — the page supplies its own.
   * Used only by the qkey.co coming-soon page, which carries a deliberately
   * stripped header and footer. Wrapping it in the marketing shell would give
   * it a nav offering six unreleased product pages, which is the opposite of
   * what a coming-soon page is for.
   */
  bare: boolean
  themeClass: string
  children: ReactNode
}) {
  if (bare) {
    return (
      <div className={themeClass}>
        {children}
      </div>
    )
  }

  if (isQkeyBrand) {
    return (
      <div className={`${themeClass} pb-12`}>
        <ExperimentalFeatureModal />
        <QkeySiteShell>{children}</QkeySiteShell>
        <AddressStatusBar />
      </div>
    )
  }

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col ${themeClass} pb-12`}>
      {/* QSAVE editorial design: no particle layer on the QSAVE brand. */}
      {getDomainConfig().name !== 'QSAVE Project' && <ParticleBackground />}
      <ExperimentalFeatureModal />
      <Navigation />
      {children}
      <Footer />
      <AddressStatusBar />
    </div>
  )
}

function App() {
  const { isAuthenticated } = useAuthStore()
  const location = useLocation()

  // Brand-aware tab title + favicon + description for the multi-domain app.
  // Overrides the QKey defaults in index.html; canonical source (also honours the
  // ?domain preview override on staging/localhost via getCurrentDomain()).
  useEffect(() => {
    const meta = getBrandMeta()
    document.title = meta.title
    const desc = document.querySelector('meta[name="description"]')
    if (desc) desc.setAttribute('content', meta.description)
    ;(['link[rel="icon"]', 'link[rel="shortcut icon"]', 'link[rel="apple-touch-icon"]'] as const).forEach((sel) => {
      const link = document.querySelector(sel) as HTMLLinkElement | null
      if (link) link.href = meta.favicon
    })
  }, [])

  // ADD THIS: Get theme class based on domain
  const themeClass = getDomainThemeClass()
  const currentDomain = getCurrentDomain().replace(/^www\./, '')
  const isRetiredDomain = currentDomain === 'opo.it.com' || currentDomain === 'nwf.so'

  // WHICH BRAND THIS REQUEST IS. Derived once and used for the go-quiet branch,
  // the homepage choice AND the chrome, so none of them can disagree — a page
  // rendering the QKey homepage inside QSAVE chrome was the failure mode worth
  // designing out.
  //
  // DERIVED BEFORE THE GO-QUIET CHECK BELOW, and that ordering is load-bearing.
  // It used to sit after, which meant the quiet branch returned before the brand
  // was known and could only serve the one generic HoldingPage. Production has
  // VITE_PUBLIC_QUIET=true, so that early return was silently pre-empting the
  // entire router: qkey.co shipped the old bare holding page and none of the
  // routing below ever ran.
  //
  // Uses getCurrentDomain() (not raw hostname) so the ?domain=<host> preview
  // override picks the right brand on staging/localhost. Ignored on prod.
  const isQkeyBrand =
    !currentDomain.includes('qsave.org') &&
    !currentDomain.includes('qday.is') &&
    !currentDomain.includes('pqp.to')

  // Go-quiet: production hosts show a holding page when VITE_PUBLIC_QUIET === 'true'.
  // Never applies to staging.<brand> hosts or localhost (full clean site for review/dev).
  //
  // QKey gets the 2026 holding page, which supersedes HoldingPage for this brand
  // — same compliance notice, actual branding, real contact routes. The other
  // brands keep the generic one until they have their own.
  if (isPublicQuietMode()) {
    return isQkeyBrand ? (
      <div className={themeClass}>
        <Suspense fallback={<PageLoader />}>
          <ComingSoonPage />
        </Suspense>
      </div>
    ) : (
      <HoldingPage />
    )
  }

  if (isRetiredDomain) {
    return <RetiredDomainPage />
  }

  /**
   * QKEY IS IN HOLDING MODE. THIS IS THE ONE SWITCH.
   *
   * While true, qkey.co serves the holding page at EVERY path and the rest of
   * the site is unreachable — no nav, no product pages, no /terms, nothing.
   *
   * That is stricter than "the homepage is a holding page", and deliberately
   * so: the first attempt only replaced `/`, which left every inner route live
   * and meant a single footer link into /terms opened the entire site. A
   * holding page with any reachable route behind it is not a holding page.
   *
   * The one exception is /preview, which serves the finished 2026 homepage so
   * it can be reviewed and signed off. It is unlisted and linked from nowhere.
   *
   * TO GO FULLY PUBLIC: set this to false. Every route returns, and
   * getHomePage() below serves QkeyHomePage instead of the holding page.
   */
  const QKEY_HOLDING_MODE = true
  const qkeyHolding = isQkeyBrand && QKEY_HOLDING_MODE

  // The holding page owns its whole viewport and supplies its own header and
  // footer, so the shared chrome is suppressed wherever it renders. /preview
  // keeps the marketing shell so the real site can be judged as it will ship.
  const isPreview = location.pathname === '/preview'
  const isQkeyRoot = qkeyHolding && !isPreview

  // ADD THIS: Function to determine which homepage to show
  const getHomePage = () => {
    const hostname = currentDomain

    // Check for each domain and return appropriate homepage
    if (hostname.includes('qsave.org')) return <QSAVEHomePage />
    if (hostname.includes('qday.is')) return <QDayHomePage />
    if (hostname.includes('pqp.to')) return <PQPHomePage />

    // qkey.co (and localhost, which defaults to QKey).
    //
    // THE PUBLIC FRONT DOOR IS THE COMING-SOON PAGE while the full site is in
    // preparation — qsave.org links here, and that link has to land somewhere
    // credible. The finished 2026 homepage is NOT deleted; it is served at
    // /preview (route below) so it can be reviewed and signed off, and this
    // line is the single edit that swaps it in when that happens.
    return <ComingSoonPage />
  }

  return (
    <ErrorBoundary fallback={<div style={{ padding: '20px', textAlign: 'center' }}>App failed to load. Please refresh or check console.</div>}>
      {/* WRAP EVERYTHING IN DomainRouter */}
      <ScrollToTop />
      <DomainRouter>
        <Chrome isQkeyBrand={isQkeyBrand} bare={isQkeyRoot} themeClass={themeClass}>
          {/* The QKey shell supplies its own height and spacing; the legacy
              chrome needs the flex-1 and the 64px offset under its fixed nav.
              The bare (coming-soon) case wants neither. */}
          <main className={isQkeyBrand ? '' : 'flex-1 pt-16'}>
            <Suspense fallback={<PageLoader />}>
              {qkeyHolding ? (
                /* HOLDING MODE — the entire QKey route table is these two lines.
                   The catch-all is what makes it a holding page rather than a
                   holding homepage: no typed URL, stale link or stray <Link>
                   can reach the site behind it. See QKEY_HOLDING_MODE above. */
                <Routes>
                  <Route path="/preview" element={<QkeyHomePage />} />
                  <Route path="*" element={<ComingSoonPage />} />
                </Routes>
              ) : (
              <Routes>
                {/* CHANGE THE HOME ROUTE TO USE getHomePage() */}
                <Route path="/" element={getHomePage()} />
                {/* /b was the A/B variant; it is now the home page itself. */}
                <Route path="/b" element={<Navigate to="/" replace />} />

                {/* All other routes stay the same */}
                <Route path="/pqp" element={<PQPPage />} />
                <Route path="/quantum-keys" element={<QuantumKeysPage />} />
                <Route path="/hd-wallet" element={<HDWalletPage />} />
                <Route path="/wallets" element={<MultiWalletDashboard />} />
                <Route path="/qd3-registration" element={<QD3ProtectionPage />} />
                <Route path="/pq-exchange" element={<QuantumVaultPage />} />
                <Route path="/uspq" element={<USPQPage />} />
                <Route path="/quantum-vault-pqp" element={<QuantumVaultPQPPage />} />
                <Route path="/qusd" element={<Navigate to="/uspq" replace />} />
                <Route path="/pq-chain" element={<QChainPage />} />
                <Route path="/tokenomics" element={<TokenomicsPage />} />
                <Route path="/philanthropic-foundation" element={<PhilanthropicFoundationPage />} />
                <Route path="/non-sovereign-wealth-fund" element={<Navigate to="/philanthropic-foundation" replace />} />
                <Route path="/standards-mark" element={<StandardsMarkPage />} />
                <Route path="/standards-mark/contact" element={<StandardsMarkContactPage />} />
                <Route path="/enterprise" element={<EnterprisePage />} />
                <Route path="/transaction" element={<TransactionPage />} />
                <Route path="/bitcoin-tools" element={<BitcoinToolsPage />} />
                <Route path="/faq" element={<FAQPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/whitepapers" element={<WhitepapersPage />} />
                <Route path="/whitepapers/:id" element={<WhitepaperViewerPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/invoice-generator" element={<InvoiceGeneratorPage />} />
                <Route path="/certification" element={<CertificationProgramPage />} />
                <Route path="/tools" element={<ToolsPage />} />
                <Route path="/brand-guidelines" element={<BrandGuidelinesPage />} />
                <Route path="/image-test" element={<ImageTest />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/permissionless" element={<PermissionlessPage />} />
                <Route path="/water-drop" element={<WaterDropPage />} />
                <Route path="/token" element={<TokenPage />} />
                <Route path="/protection-workflow" element={<ProtectionWorkflowPage />} />
                <Route 
                  path="/technical-docs" 
                  element={<TechnicalDocsPage />} 
                />
                <Route path="/qsave" element={<QSavePage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/implementers" element={<ImplementersPage />} />
                <Route path="/wallet-hardening" element={<WalletHardeningPage />} />
                <Route path="/automatic-migration" element={<AutomaticMigrationPage />} />
                <Route path="/recovery" element={<RecoveryPage />} />
                <Route path="/partners" element={<PartnersPage />} />
                <Route path="/output-analyzer" element={<OutputAnalyzerTestPage />} />
                <Route path="/address-test-table" element={<AddressTestTablePage />} />
                <Route 
                  path="/token/kyc-compliance" 
                  element={<KycCompliancePage />} 
                />
                <Route 
                  path="/token/restricted-countries" 
                  element={<RestrictedCountriesPage />} 
                />
                <Route
                  path="/token/regulatory-info"
                  element={<RegulatoryInfoPage />}
                />
                <Route
                  path="/geo-blocked"
                  element={<GeoBlockedPage />}
                />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/test-report" element={<TestReportPage />} />
                <Route path="/lattice-visualization" element={<LatticeCryptoVisualization />} />
                <Route path="/docs/viewer/:docPath/*" element={<DocumentViewer />} />

                {/* The finished 2026 homepage, while / serves the coming-soon
                    page. This is the review URL — when the site goes fully
                    public, point getHomePage() at QkeyHomePage instead. */}
                <Route path="/preview" element={<QkeyHomePage />} />

                {/* The pre-redesign qkey.co homepage, kept reachable so the old
                    and new designs can be compared side by side during review.
                    Remove this route once the redesign is signed off. */}
                <Route path="/legacy-home" element={<HomePageModern />} />

                {/* Catch-all: repairs trailing punctuation, else renders a 404. */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
              )}
            </Suspense>
          </main>
        </Chrome>
      </DomainRouter>
    </ErrorBoundary>
  )
}

export default App
